
import React from 'react';
import { Button } from '@/components/ui/button';
import { Menu } from 'lucide-react';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useAuth } from '@/contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import { useSidebar } from "@/components/ui/sidebar";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAffiliateInfo } from '@/hooks/useAffiliate';
import { useToast } from '@/components/ui/use-toast';

// Language items for the dropdown
const LANGUAGES = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "vi", label: "Tiếng Việt", flag: "🇻🇳" },
  { code: "zh", label: "中文", flag: "🇨🇳" },
  { code: "th", label: "ไทย", flag: "🇹🇭" },
  { code: "ko", label: "한국어", flag: "🇰🇷" },
  { code: "ja", label: "日本語", flag: "🇯🇵" }
];

export const MobileAffiliateTopbar = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toggleSidebar } = useSidebar();
  const { language, setLanguage } = useLanguage();
  const { toast } = useToast();
  const { data: affiliateInfo } = useAffiliateInfo();

  const copyReferralLink = () => {
    if (!affiliateInfo?.referral_code) return;
    
    const baseUrl = window.location.origin;
    const referralUrl = `${baseUrl}/?ref=${affiliateInfo.referral_code}`;
    
    navigator.clipboard.writeText(referralUrl);
    toast({
      title: "Copied to clipboard",
      description: "Referral link has been copied to clipboard."
    });
  };

  // Menu items for the dropdown
  const menuItems = [
    { label: t('dashboard'), path: '/' },
    { label: t('affiliate_dashboard'), path: '/affiliate' },
    { label: t('affiliate_referrals'), path: '/affiliate/referrals' },
    { label: t('affiliate_links'), path: '/affiliate/links' },
    { label: t('settings'), path: '/settings' },
  ];

  return (
    <header className="h-14 flex items-center justify-between px-4 border-b border-border bg-background" style={{ paddingTop: 'env(safe-area-inset-top)', height: 'calc(3.5rem + env(safe-area-inset-top))' }}>
      <Button 
        variant="ghost" 
        size="icon" 
        className="h-9 w-9" 
        onClick={toggleSidebar}
        aria-label="Toggle sidebar">
        <Menu size={20} />
      </Button>
      
      <h1 className="text-lg font-semibold">{t("affiliate_program")}</h1>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="relative h-9 w-9 rounded-full">
            <Avatar className="h-9 w-9">
              <AvatarFallback className="bg-primary/20 text-primary-foreground">
                {user?.user_metadata?.name?.split(' ')
                  .map(n => n[0])
                  .join('')
                  .toUpperCase() || 'TB'}
              </AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56" align="end" forceMount>
          <DropdownMenuLabel>{user?.user_metadata?.name || 'User'}</DropdownMenuLabel>
          
          {/* Referral code - Show only for active affiliates */}
          {affiliateInfo?.status === 'active' && (
            <>
              <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                {t("your_referral_code")}
              </DropdownMenuLabel>
              <div className="px-2 py-1.5 flex items-center justify-between">
                <code className="text-xs font-mono bg-muted px-1.5 py-0.5 rounded">
                  {affiliateInfo.referral_code}
                </code>
                <Button variant="ghost" size="sm" className="h-6 px-2" onClick={copyReferralLink}>
                  {t("copy")}
                </Button>
              </div>
              <DropdownMenuSeparator />
            </>
          )}
          
          {/* Language Selection */}
          <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
            {t("change_language")}
          </DropdownMenuLabel>
          <div className="px-2 py-1.5">
            <div className="grid grid-cols-2 gap-1">
              {LANGUAGES.map((lang) => (
                <Button
                  key={lang.code}
                  variant="ghost"
                  size="sm"
                  className={`justify-start h-8 ${language === lang.code ? 'bg-accent text-accent-foreground font-semibold' : ''}`}
                  onClick={() => setLanguage(lang.code)}
                >
                  <span className="mr-2 text-base">{lang.flag}</span>
                  <span className="text-xs">{lang.label}</span>
                </Button>
              ))}
            </div>
          </div>
          
          <DropdownMenuSeparator />
          
          {/* Menu Navigation */}
          <DropdownMenuItem onClick={() => navigate('/')}>
            {t("back_to_main_dashboard")}
          </DropdownMenuItem>
          
          {menuItems.map((item) => (
            <DropdownMenuItem key={item.path} asChild>
              <Link to={item.path}>
                {item.label}
              </Link>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
};
